import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import type { MathQuestion as MathType } from '@/types/question-types';
import { renderMathInText } from '@/utils/renderMath';
import styles from './MathQuestion.module.scss';

interface MathQuestionProps {
  question: MathType;
  userAnswer?: string;
  onAnswer: (answer: string) => void;
  showExplanation?: boolean;
  isReviewMode?: boolean;
  mathLiveReady?: boolean;
}

export function MathQuestion({
  question,
  userAnswer,
  onAnswer,
  showExplanation = false,
  isReviewMode = false,
  mathLiveReady = false,
}: MathQuestionProps) {
  const [inputMode, setInputMode] = useState<'latex' | 'visual'>('latex');
  const [latex, setLatex] = useState(userAnswer || '');
  const mathFieldRef = useRef<any>(null);

  const onAnswerRef = useRef(onAnswer);

  // Keep onAnswer fresh
  useEffect(() => {
    onAnswerRef.current = onAnswer;
  }, [onAnswer]);

  // Initialize MathLive when switching to visual mode
  useEffect(() => {
    const mf = mathFieldRef.current;
    if (inputMode === 'visual' && mathLiveReady && mf) {
      // Set initial value
      mf.value = latex;

      const listener = (evt: any) => {
        const newLatex = evt.target.value;
        setLatex(newLatex);
        onAnswerRef.current(newLatex);
      };

      mf.addEventListener('input', listener);

      return () => {
        mf.removeEventListener('input', listener);
      };
    }
  }, [inputMode, mathLiveReady]); // Re-run only when mode changes or libs loads

  // Sync state changes to MathField (one-way sync for external updates)
  useEffect(() => {
    const mf = mathFieldRef.current;
    if (inputMode === 'visual' && mathLiveReady && mf) {
      // Only update if value is different to avoid cursor jumps
      if (mf.value !== latex) {
        mf.value = latex;
      }
    }
  }, [latex, inputMode, mathLiveReady]);

  // Update latex when userAnswer prop changes
  useEffect(() => {
    if (userAnswer !== undefined && userAnswer !== latex) {
      setLatex(userAnswer);
    }
  }, [userAnswer]);

  const handleLatexChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newLatex = e.target.value;
    setLatex(newLatex);
    onAnswer(newLatex);
  };

  // Parse question text for $$...$$ blocks (display math) and $...$ (inline math)
  const renderQuestionWithMath = () => {
    const parts: (string | React.ReactElement)[] = [];
    let currentText = question.question_text;
    let key = 0;

    // First, split by display math ($$...$$)
    const displayMathRegex = /\$\$(.*?)\$\$/g;
    let lastIndex = 0;
    let match;

    while ((match = displayMathRegex.exec(currentText)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        const textBefore = currentText.slice(lastIndex, match.index);
        parts.push(
          <span key={key++}>{renderInlineMath(textBefore, key)}</span>
        );
      }

      // Add the display math
      parts.push(<BlockMath key={key++} math={match[1]} />);

      lastIndex = displayMathRegex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < currentText.length) {
      const remaining = currentText.slice(lastIndex);
      parts.push(<span key={key++}>{renderInlineMath(remaining, key)}</span>);
    }

    return parts;
  };

  // Render inline math within text
  const renderInlineMath = (text: string, startKey: number) => {
    const parts: (string | React.ReactElement)[] = [];
    const inlineMathRegex = /\$(.*?)\$/g;
    let lastIndex = 0;
    let match;
    let key = startKey;

    while ((match = inlineMathRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Add the inline math
      parts.push(<InlineMath key={key++} math={match[1]} />);

      lastIndex = inlineMathRegex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  return (
    <div className={styles.mathQuestion}>
      <div className={styles.mathQuestion__question}>
        <h3 className={styles.mathQuestion__questionText}>
          {renderQuestionWithMath()}
        </h3>
      </div>

      {!isReviewMode && (
        <>
          <div className={styles.mathQuestion__inputToggle}>
            <button
              className={`${styles.mathQuestion__toggleButton} ${inputMode === 'latex'
                ? styles['mathQuestion__toggleButton--active']
                : ''
                }`}
              onClick={() => setInputMode('latex')}
              type="button"
            >
              LaTeX Input
            </button>
            <button
              className={`${styles.mathQuestion__toggleButton} ${inputMode === 'visual'
                ? styles['mathQuestion__toggleButton--active']
                : ''
                }`}
              onClick={() => setInputMode('visual')}
              type="button"
              disabled={!mathLiveReady}
            >
              Visual Editor {!mathLiveReady && '(Loading...)'}
            </button>
          </div>

          <div className={styles.mathQuestion__input}>
            {inputMode === 'visual' ? (
              mathLiveReady ? (
                <math-field
                  ref={mathFieldRef}
                  class={styles.mathQuestion__mathField}
                >
                  {latex}
                </math-field>
              ) : (
                <div className={styles.mathQuestion__loading}>
                  Loading visual editor...
                </div>
              )
            ) : (
              <>
                <textarea
                  value={latex}
                  onChange={handleLatexChange}
                  placeholder="Enter LaTeX (e.g., \\frac{1}{2}, x^2, \\sqrt{x})"
                  className={styles.mathQuestion__textarea}
                  rows={3}
                />
                <div className={styles.mathQuestion__hint}>
                  Examples: \frac{"{a}"}{"{b}"} for fractions, x^2 for
                  exponents, \sqrt{"{x}"} for square root
                </div>
              </>
            )}
          </div>

          {latex && inputMode === 'latex' && (
            <motion.div
              className={styles.mathQuestion__preview}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
            >
              <div className={styles.mathQuestion__previewHeader}>
                <strong>Preview:</strong>
              </div>
              <div className={styles.mathQuestion__previewContent}>
                <BlockMath math={latex} />
              </div>
            </motion.div>
          )}
        </>
      )}

      {isReviewMode && userAnswer && (
        <div className={styles.mathQuestion__reviewAnswer}>
          <div className={styles.mathQuestion__reviewLabel}>Your answer:</div>
          <div className={styles.mathQuestion__reviewContent}>
            <BlockMath math={userAnswer} />
          </div>
        </div>
      )}

      {showExplanation && (
        <motion.div
          className={styles.mathQuestion__explanation}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          {(() => {
            const normalizedUser = (userAnswer || '').replace(/\s/g, '');
            const isCorrect = question.content.acceptedAnswers.some(accepted =>
              accepted.replace(/\s/g, '') === normalizedUser
            );

            return (
              <div className={`${styles.mathQuestion__result} ${styles[`mathQuestion__result--${isCorrect ? 'correct' : 'incorrect'}`]
                }`}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </div>
            );
          })()}

          <div className={styles.mathQuestion__explanationHeader}>
            <strong>Explanation:</strong>
          </div>
          <p className={styles.mathQuestion__explanationText}>
            {renderMathInText(question.explanation)}
          </p>
          {question.content.acceptedAnswers.length > 0 && (
            <div className={styles.mathQuestion__correctAnswers}>
              <div className={styles.mathQuestion__correctLabel}>
                Accepted answers:
              </div>
              {question.content.acceptedAnswers.map((answer, index) => (
                <div
                  key={index}
                  className={styles.mathQuestion__correctAnswer}
                >
                  <BlockMath math={answer} />
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
